import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, KeyRound, Lock, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

const ROLE_OPTIONS = [
  { value: "doctor", label: "Doctor" },
  { value: "clinic", label: "Clinic" },
  { value: "admin", label: "Admin" },
];

export default function StaffLogin() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("doctor");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, role });
      setSession(data.access_token, { ...data.user, role: data.role });
      toast.success(`Welcome back, ${data.user.name || "user"}`);
      navigate(`/${data.role}/dashboard`, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg-pattern flex flex-col">
      <header className="px-6 lg:px-10 h-16 flex items-center">
        <Link
          to="/"
          data-testid="staff-login-back-link"
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-border rounded-3xl shadow-sm p-7 sm:p-9">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
            <KeyRound className="h-3.5 w-3.5" /> Staff sign-in
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-7">
            Sign in to your Healthink workspace.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="staff-login-form">
            <div>
              <Label htmlFor="role" className="text-sm font-medium mb-2 inline-block">
                I am a
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  id="role"
                  data-testid="staff-role-select-trigger"
                  className="h-14 rounded-xl bg-white"
                >
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`staff-role-option-${r.value}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 inline-block">
                Email
              </Label>
              <div className="flex items-stretch border border-border rounded-xl focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all overflow-hidden bg-white">
                <span className="px-3.5 inline-flex items-center bg-secondary text-secondary-foreground border-r border-border">
                  <Mail className="h-4 w-4" />
                </span>
                <Input
                  id="email"
                  data-testid="staff-email-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@telemed.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base rounded-none"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 inline-block">
                Password
              </Label>
              <div className="flex items-stretch border border-border rounded-xl focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all overflow-hidden bg-white">
                <span className="px-3.5 inline-flex items-center bg-secondary text-secondary-foreground border-r border-border">
                  <Lock className="h-4 w-4" />
                </span>
                <Input
                  id="password"
                  data-testid="staff-password-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base rounded-none"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="staff-login-submit-btn"
              disabled={loading}
              className="w-full h-14 rounded-full text-base bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {/* <div
            data-testid="staff-demo-creds"
            className="mt-7 rounded-2xl bg-secondary/60 p-4 text-xs text-muted-foreground leading-relaxed"
          >
            <div className="font-semibold text-foreground mb-1.5">Demo credentials</div>
            <div>Doctor — dr.sharma@telemed.com / Doctor@123</div>
            <div>Clinic — apollo.koramangala@telemed.com / Clinic@123</div>
            <div>Admin — admin@telemed.com / Admin@123</div>
          </div> */}
        </div>
      </main>
    </div>
  );
}
